import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, setAccessToken } from '../services/api';
import toast from 'react-hot-toast';

// ── Google Sign-In helper ─────────────────────────────────────────────────────
// Uses redirect flow (not popup) because:
//   - Safari/iOS blocks popups opened from async callbacks
//   - Safari ITP blocks cross-origin popups used by Firebase signInWithPopup
//   - signInWithRedirect works reliably across all browsers including Safari
async function getGoogleIdToken() {
  const { initializeApp, getApps } = await import('firebase/app');
  const {
    getAuth,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    GoogleAuthProvider
  } = await import('firebase/auth');

  const config = {
    apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId:             process.env.REACT_APP_FIREBASE_APP_ID,
  };

  const app      = getApps().length ? getApps()[0] : initializeApp(config);
  const auth     = getAuth(app);
  const provider = new GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');

  // Detect Safari / iOS — use redirect flow which works reliably
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    || /iPad|iPhone|iPod/.test(navigator.userAgent);

  if (isSafari) {
    // Check if we're returning from a redirect (page was reloaded by Firebase)
    const redirectResult = await getRedirectResult(auth).catch(() => null);
    if (redirectResult?.user) {
      return await redirectResult.user.getIdToken();
    }
    // Otherwise, kick off the redirect — page will reload, then re-enter above
    await signInWithRedirect(auth, provider);
    return null; // never reached — page navigates away
  }

  // Non-Safari: popup works fine
  const result  = await signInWithPopup(auth, provider);
  const idToken = await result.user.getIdToken();
  return idToken;
}

// ── Shared Google Button ──────────────────────────────────────────────────────
function GoogleButton({ onClick, loading, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      style={{
        width: '100%', padding: '11px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        background: 'white', border: '1px solid var(--border)',
        cursor: loading ? 'not-allowed' : 'pointer',
        fontSize: 13, fontWeight: 600, letterSpacing: '0.04em',
        fontFamily: "'Josefin Sans', sans-serif",
        color: 'var(--ink)', transition: 'border-color 0.2s',
        opacity: loading ? 0.7 : 1,
      }}
      onMouseEnter={e => { if (!loading) e.currentTarget.style.borderColor = 'var(--gold)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      {/* Google SVG icon */}
      <svg width="18" height="18" viewBox="0 0 48 48">
        <path fill="#FFC107" d="M43.6 20H24v8h11.3C33.6 33.1 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.7-.1-4z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-1.9 13.5-5l-6.2-5.2C29.4 35.6 26.8 36 24 36c-5.2 0-9.6-2.9-11.3-7.1l-6.6 5.1C9.8 39.8 16.4 44 24 44z"/>
        <path fill="#1976D2" d="M43.6 20H24v8h11.3c-.9 2.4-2.5 4.4-4.6 5.8l6.2 5.2C41.2 35.5 44 30.2 44 24c0-1.3-.1-2.7-.4-4z"/>
      </svg>
      {loading ? 'Connecting...' : (label || 'Continue with Google')}
    </button>
  );
}

function Divider() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      <span style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>or</span>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  );
}

// ── Password strength ─────────────────────────────────────────────────────────
function checkStrength(pw) {
  return {
    length:    pw.length >= 8,
    uppercase: /[A-Z]/.test(pw),
    lowercase: /[a-z]/.test(pw),
    number:    /\d/.test(pw),
    special:   /[!@#$%^&*(),.?":{}|<>]/.test(pw),
  };
}

function StrengthBar({ password }) {
  if (!password) return null;
  const checks = checkStrength(password);
  const passed = Object.values(checks).filter(Boolean).length;
  const colors = ['#f44336', '#ff9800', '#ffc107', '#8bc34a', '#4caf50'];
  const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 3, marginBottom: 5 }}>
        {[1,2,3,4,5].map(i => (
          <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= passed ? colors[passed - 1] : 'var(--border)', transition: 'background 0.2s' }} />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
        <div style={{ fontSize: 10, color: colors[passed - 1] || 'var(--muted)', fontWeight: 700 }}>
          {labels[passed - 1] || ''}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[[checks.length,'8+'], [checks.uppercase,'A-Z'], [checks.lowercase,'a-z'], [checks.number,'0-9'], [checks.special,'!@#']].map(([ok, label]) => (
            <span key={label} style={{ fontSize: 9, color: ok ? '#4caf50' : 'rgba(0,0,0,0.25)', fontWeight: 700 }}>
              {ok ? '✓' : '○'} {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Google Login Button Component ────────────────────────────────────────────
function GoogleLoginButton({ navigate, label }) {
  const { updateUser } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleGoogle = async () => {
    setLoading(true);
    try {
      // 1. Get Google ID token (popup on Chrome, redirect on Safari)
      const idToken = await getGoogleIdToken();

      // Safari redirect flow — page is navigating away, idToken is null
      if (!idToken) return; // loading stays true until page reloads

      // 2. Send to backend
      const { data } = await authAPI.googleAuth(idToken);

      // 3. Set access token in memory
      setAccessToken(data.accessToken);

      // 4. Update AuthContext user state + localStorage
      try { localStorage.setItem('wb_user', JSON.stringify(data.user)); } catch {}
      updateUser(data.user);

      // 5. Navigate
      if (data.user.role === 'admin') {
        navigate('/admin');
      } else {
        toast.success(`Welcome, ${data.user.name.split(' ')[0]}! 👋`);
        navigate('/browse');
      }
    } catch (err) {
      // User closed popup — don't show error
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') return;
      const msg = err?.response?.data?.message || err?.message || 'Google sign-in failed. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return <GoogleButton onClick={handleGoogle} loading={loading} label={label} />;
}

// ── LOGIN ─────────────────────────────────────────────────────────────────────
export function LoginPage() {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false); // email not yet verified
  const { login } = useAuth();
  const navigate  = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      // User is logged in regardless of email verification
      if (!user.isEmailVerified) {
        // Don't block — just show inline reminder after redirect
        navigate(user.role === 'admin' ? '/admin' : '/browse');
        toast('📧 Please verify your email when you get a chance. Check your inbox.', {
          duration: 6000,
          style: { background: '#fff8e1', border: '1px solid #ffc107', color: '#856404' }
        });
      } else {
        navigate(user.role === 'admin' ? '/admin' : '/browse');
      }
    } catch (err) {
      const msg = err.response?.data?.message || '';
      // If server still returns 403 for unverified (old deploy), show resend option
      if (err.response?.status === 403 && msg.toLowerCase().includes('verify')) {
        setNeedsVerify(true);
      } else {
        toast.error(msg || 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Resend verification email inline
  if (needsVerify) {
    return (
      <div style={s.page}>
        <div style={{ ...s.card, textAlign: 'center' }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>📧</div>
          <h2 style={s.title}>Verify Your Email</h2>
          <p style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.9, margin: '12px 0 24px' }}>
            We sent a verification link to <strong>{email}</strong>.<br />
            Click the link to activate your account, then log in.
          </p>
          <ResendButton email={email} />
          <button
            className="btn btn-ghost btn-sm w-full"
            style={{ marginTop: 12 }}
            onClick={() => setNeedsVerify(false)}
          >
            ← Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.header}>
          <h2 style={s.title}>Welcome Back</h2>
          <p style={s.sub}>Sign in to your account</p>
        </div>

        <GoogleLoginButton navigate={navigate} label="Continue with Google" />
        <Divider />

        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              className="input" type="email" value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com" required autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                className="input"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••" required
                autoComplete="current-password"
                style={{ paddingRight: 48 }}
              />
              <button type="button" onClick={() => setShowPw(!showPw)} style={s.eyeBtn} tabIndex={-1}>
                {showPw ? '🙈' : '👁'}
              </button>
            </div>
          </div>

          <div style={{ textAlign: 'right', marginBottom: 20 }}>
            <Link to="/forgot-password" style={{ fontSize: 12, color: 'var(--gold)' }}>Forgot password?</Link>
          </div>

          <button type="submit" className="btn btn-primary w-full btn-lg" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p style={s.footer}>
          Don't have an account? <Link to="/register" style={{ color: 'var(--gold)', fontWeight: 600 }}>Create one free</Link>
        </p>
      </div>
    </div>
  );
}

// ── REGISTER ──────────────────────────────────────────────────────────────────
export function RegisterPage() {
  const location = useLocation();
  const refParam = new URLSearchParams(location.search).get('ref') || '';
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '', email: '', password: '', confirmPassword: '',
    college: '', phone: '', referralCode: refParam
  });
  const [showPw,          setShowPw]         = useState(false);
  const [loading,         setLoading]        = useState(false);
  const [registered,      setRegistered]     = useState(false);
  const [referralApplied, setReferralApplied]= useState(false);
  const [step,            setStep]           = useState('prompt'); // 'prompt' | 'form'
  const { register } = useAuth();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { toast.error('Passwords do not match.'); return; }
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters.'); return; }
    if (form.phone && !/^[6-9]\d{9}$/.test(form.phone)) {
      toast.error('Enter a valid 10-digit Indian mobile number.'); return;
    }
    setLoading(true);
    try {
      const res = await register({
        name: form.name, email: form.email, password: form.password,
        college: form.college, phone: form.phone,
        referralCode: form.referralCode.trim().toUpperCase() || undefined
      });
      setReferralApplied(res?.referralApplied || false);
      setRegistered(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally { setLoading(false); }
  };

  // ── Post-registration: show email check screen ────────────────────────────
  if (registered) {
    return (
      <div style={s.page}>
        <div style={{ ...s.card, textAlign: 'center' }}>
          <div style={{ fontSize: 60, marginBottom: 16 }}>📧</div>
          <h2 style={s.title}>Check Your Inbox</h2>
          {referralApplied && (
            <div style={{ background: '#e8f5e9', border: '1px solid #4caf50', padding: '10px 16px', margin: '12px 0', fontSize: 13, color: '#1b5e20' }}>
              🎁 Referral discount applied! You'll get <strong>₹200 off</strong> your first order automatically.
            </div>
          )}
          <p style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.9, margin: '12px 0 20px' }}>
            We sent a verification link to <strong>{form.email}</strong>.<br />
            You can <strong>log in now</strong> — verify your email anytime later.<br />
            Email verification is only required when placing an order.
          </p>
          <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', padding: '12px 16px', fontSize: 12, color: 'var(--muted)', lineHeight: 1.8, marginBottom: 20 }}>
            📬 Check spam/junk if you don't see it. The link expires in <strong>24 hours</strong>.
          </div>

          {/* Go to login — primary action */}
          <button
            className="btn btn-primary w-full btn-lg"
            onClick={() => navigate('/login')}
            style={{ marginBottom: 10 }}
          >
            Go to Login →
          </button>

          <ResendButton email={form.email} />
        </div>
      </div>
    );
  }

  // ── Step 1: College prompt ──────────────────────────────────────────────────
  if (step === 'prompt') {
    return (
      <div style={s.page}>
        <div style={{ ...s.card, maxWidth: 480, textAlign: 'center' }}>

          {/* Logo */}
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, fontStyle: 'italic', color: 'var(--ink)', marginBottom: 4 }}>
            Warb<span style={{ color: 'var(--gold)' }}>adobe</span>
          </div>
          <div style={{ fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 32 }}>Rentals</div>

          {/* Headline */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 12 }}>
            Before you join
          </div>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 30, fontWeight: 400, color: 'var(--ink)', lineHeight: 1.2, margin: '0 0 16px' }}>
            Are you a college student?
          </h2>
          <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.9, margin: '0 0 28px' }}>
            Sign up with your college email (<span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink)' }}>@nitj.ac.in</span>, <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink)' }}>@lpu.ac.in</span>, <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink)' }}>@ptu.ac.in</span> etc.) to unlock <strong style={{ color: 'var(--ink)' }}>unlimited orders</strong> from day one — no waiting, no documents.
          </p>

          {/* College CTA */}
          <div style={{ background: 'linear-gradient(135deg, #f3e5f5, #e8f5e9)', border: '1px solid #ce93d8', padding: 20, marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#4a148c', marginBottom: 10 }}>
              🎓 College Student
            </div>
            <div style={{ fontSize: 12, color: '#333', lineHeight: 1.7, marginBottom: 16 }}>
              Instant unlimited access · No identity documents · Auto-verified in seconds
            </div>
            <GoogleLoginButton navigate={navigate} label="Sign up with College Google Account" />
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10 }}>
              Uses your college Google account (e.g. <span style={{ fontFamily: 'monospace' }}>you@nitj.ac.in</span>)
            </div>
          </div>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)' }}>or</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>

          {/* Non-college CTA */}
          <div style={{ border: '1px solid var(--border)', padding: 20, marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>
              Not a student / Using personal email
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 16 }}>
              Start with orders up to ₹399. Your limit grows as you build your Warba Score through successful rentals.
            </div>
            <button onClick={() => setStep('form')}
              style={{ width: '100%', padding: '12px 0', fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--cream)', border: 'none', cursor: 'pointer' }}>
              Continue with Personal Email →
            </button>
          </div>

          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
            Already have an account? <a href="/login" style={{ color: 'var(--gold)', fontWeight: 600, textDecoration: 'none' }}>Log in</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={{ ...s.card, maxWidth: 520 }}>
        <div style={s.header}>
          <h2 style={s.title}>Join Warbadobe</h2>
          <p style={s.sub}>Luxury rentals for students & young adults</p>
        </div>

        <GoogleLoginButton navigate={navigate} />
        <Divider />

        <form onSubmit={handleRegister}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="input" value={form.name} onChange={update('name')} placeholder="Your name" required autoComplete="name" />
            </div>
            <div className="form-group">
              <label className="form-label">Phone <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 9 }}>(optional)</span></label>
              <input className="input" type="tel" value={form.phone} onChange={update('phone')} placeholder="9876543210" maxLength={10} autoComplete="tel" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Email Address *</label>
            <input className="input" type="email" value={form.email} onChange={update('email')} placeholder="your@email.com" required autoComplete="email" />
            {form.email && /\.(ac\.in|edu\.in|edu)$/.test(form.email.split('@')[1] || '') && (
              <div style={{ fontSize: 11, color: '#2d6a2d', marginTop: 5, display: 'flex', alignItems: 'center', gap: 5 }}>
                <span>✓</span> College email detected — you'll get unlimited order access automatically.
              </div>
            )}
            {form.email && form.email.includes('@') && !/\.(ac\.in|edu\.in|edu)$/.test(form.email.split('@')[1] || '') && (
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5 }}>
                Not a college email? You can verify it later on your Warba Score page.
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">College / University <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 9 }}>(optional)</span></label>
            <input className="input" value={form.college} onChange={update('college')} placeholder="Your institution" />
          </div>

          <div className="form-group">
            <label className="form-label">Password *</label>
            <div style={{ position: 'relative' }}>
              <input
                className="input"
                type={showPw ? 'text' : 'password'}
                value={form.password} onChange={update('password')}
                placeholder="Min 6 characters" required
                autoComplete="new-password" style={{ paddingRight: 48 }}
              />
              <button type="button" onClick={() => setShowPw(!showPw)} style={s.eyeBtn} tabIndex={-1}>{showPw ? '🙈' : '👁'}</button>
            </div>
            <StrengthBar password={form.password} />
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password *</label>
            <input
              className="input" type="password"
              value={form.confirmPassword} onChange={update('confirmPassword')}
              placeholder="Repeat password" required autoComplete="new-password"
            />
            {form.confirmPassword && form.password !== form.confirmPassword && (
              <div className="form-error">Passwords do not match</div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Referral Code <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 9 }}>(optional)</span></label>
            <input
              className="input" value={form.referralCode} onChange={update('referralCode')}
              placeholder="e.g. WARB1234" maxLength={8}
              style={{ textTransform: 'uppercase', letterSpacing: '0.1em' }}
            />
            {form.referralCode.length > 0 && (
              <div style={{ fontSize: 11, color: '#388e3c', marginTop: 4 }}>🎁 ₹200 will be applied to your first order!</div>
            )}
          </div>

          <button type="submit" className="btn btn-primary w-full btn-lg" style={{ marginTop: 8 }} disabled={loading || form.password.length < 6}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <p style={s.footer}>
          Already have an account? <Link to="/login" style={{ color: 'var(--gold)', fontWeight: 600 }}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}

// ── Shared: resend verification email with cooldown ───────────────────────────
function ResendButton({ email }) {
  const [sent,      setSent]      = useState(false);
  const [cooldown,  setCooldown]  = useState(0);
  const [sending,   setSending]   = useState(false);

  const handleResend = async () => {
    if (cooldown > 0 || sending) return;
    setSending(true);
    try {
      await authAPI.resendVerification({ email });
      toast.success('Verification email resent! Check your inbox.');
      setSent(true);
      // 60-second cooldown
      let secs = 60;
      setCooldown(secs);
      const t = setInterval(() => {
        secs -= 1;
        setCooldown(secs);
        if (secs <= 0) clearInterval(t);
      }, 1000);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to resend. Try again shortly.');
    } finally { setSending(false); }
  };

  return (
    <button
      className="btn btn-outline btn-sm w-full"
      onClick={handleResend}
      disabled={cooldown > 0 || sending}
      style={{ marginTop: 8 }}
    >
      {sending       ? 'Sending...'
       : cooldown > 0 ? `Resend in ${cooldown}s`
       : sent         ? '✓ Resend Email'
       :                'Resend Verification Email'}
    </button>
  );
}

// ── FORGOT PASSWORD ───────────────────────────────────────────────────────────
export function ForgotPasswordPage() {
  const [email,     setEmail]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await authAPI.forgotPassword({ email }); } catch { /* show success either way */ }
    finally { setLoading(false); setSubmitted(true); }
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        {!submitted ? (
          <>
            <div style={s.header}>
              <h2 style={s.title}>Reset Password</h2>
              <p style={s.sub}>Enter your email to receive a reset link</p>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required autoFocus />
              </div>
              <button type="submit" className="btn btn-primary w-full btn-lg" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
            <p style={s.footer}><Link to="/login" style={{ color: 'var(--gold)' }}>← Back to Login</Link></p>
          </>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🔑</div>
            <h2 style={s.title}>Check Your Email</h2>
            <p style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.9, margin: '12px 0 20px' }}>
              If an account exists for <strong>{email}</strong>, you'll receive a reset link shortly.
            </p>
            <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', padding: '12px 16px', fontSize: 11, color: 'var(--muted)', marginBottom: 20 }}>
              The link expires in <strong>1 hour</strong>. Check spam if needed.
            </div>
            <Link to="/login" className="btn btn-primary w-full">Back to Login</Link>
          </div>
        )}
      </div>
    </div>
  );
}

// ── RESET PASSWORD ────────────────────────────────────────────────────────────
export function ResetPasswordPage() {
  const location = useLocation();
  const token    = new URLSearchParams(location.search).get('token') || '';
  const navigate = useNavigate();
  const { updateUser } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [loading,  setLoading]  = useState(false);
  const [showPw,   setShowPw]   = useState(false);

  const checks = checkStrength(password);
  const pwStrong = Object.values(checks).every(Boolean);

  const handleReset = async (e) => {
    e.preventDefault();
    if (!pwStrong)            { toast.error('Please choose a stronger password.'); return; }
    if (password !== confirm) { toast.error('Passwords do not match.'); return; }
    if (!token)               { toast.error('Invalid reset link.'); return; }
    setLoading(true);
    try {
      const { data } = await authAPI.resetPassword(token, { password });
      if (data.accessToken) { setAccessToken(data.accessToken); updateUser(data.user); }
      toast.success('Password reset! You are now logged in.');
      navigate('/browse');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reset failed. The link may have expired.');
    } finally { setLoading(false); }
  };

  if (!token) return (
    <div style={s.page}>
      <div style={{ ...s.card, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h2 style={s.title}>Invalid Link</h2>
        <p style={s.sub}>This reset link is invalid or has expired.</p>
        <Link to="/forgot-password" className="btn btn-primary" style={{ marginTop: 20, display: 'inline-block' }}>Request New Link</Link>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.header}>
          <h2 style={s.title}>Set New Password</h2>
          <p style={s.sub}>Create a strong password for your account</p>
        </div>
        <form onSubmit={handleReset}>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <div style={{ position: 'relative' }}>
              <input className="input" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" required style={{ paddingRight: 48 }} />
              <button type="button" onClick={() => setShowPw(!showPw)} style={s.eyeBtn} tabIndex={-1}>{showPw ? '🙈' : '👁'}</button>
            </div>
            <StrengthBar password={password} />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <input className="input" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password" required />
            {confirm && password !== confirm && <div className="form-error">Passwords do not match</div>}
          </div>
          <button type="submit" className="btn btn-primary w-full btn-lg" disabled={loading || !pwStrong}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

const s = {
  page:   { minHeight: '80vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 'clamp(32px, 8vw, 80px) 16px' },
  card:   { width: '100%', maxWidth: 460, background: 'white', border: '1px solid var(--border)', padding: 'clamp(24px, 5vw, 40px)' },
  header: { textAlign: 'center', marginBottom: 28 },
  title:  { fontFamily: "'Cormorant Garamond', serif", fontSize: 36, fontStyle: 'italic' },
  sub:    { color: 'var(--muted)', fontSize: 13, marginTop: 8 },
  footer: { textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--muted)' },
  eyeBtn: { position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, minWidth: 36, minHeight: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' },
};